import { forwardRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { FileText, Minus, MoreVertical, Pencil, Plus } from "lucide-react";
import client, { apiErrorMessage } from "@/api/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export const formatINR = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

// Mirrors computeTotals in backend-nest/src/quotations/quotation-pricing.ts, for the live preview only;
// the server recomputes and stores the real figures.
function previewTotals(subtotal, { discountType, discountValue, gstPercent }) {
  const raw = discountType === "PERCENT" ? (subtotal * discountValue) / 100 : discountValue;
  const discountAmount = Math.min(Math.max(raw || 0, 0), subtotal);
  const gstAmount = ((subtotal - discountAmount) * (gstPercent || 0)) / 100;
  return { subtotal, discountAmount, gstAmount, grandTotal: Math.round(subtotal - discountAmount + gstAmount) };
}

function QtyStepper({ value, onChange, disabled, testId }) {
  const btn = "h-9 w-9 flex items-center justify-center rounded-lg border border-border bg-card hover:bg-secondary disabled:opacity-40 disabled:hover:bg-card transition-colors";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      <button type="button" aria-label="Decrease quantity" className={btn} disabled={disabled || value <= 1} onClick={() => onChange(value - 1)}>
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-8 text-center tabular-nums text-foreground">{value}</span>
      <button type="button" aria-label="Increase quantity" className={btn} disabled={disabled || value >= 999} onClick={() => onChange(value + 1)}>
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

const itemTitle = (it) => it.shopReference || it.specSnapshot?.productName || "Product";
const itemStyle = (it) => [it.specSnapshot?.colour, it.specSnapshot?.pleat].filter(Boolean).join(" / ");
const itemName = (it) => it.specSnapshot?.productName || it.specSnapshot?.fabric;

function ItemMenu({ item, projectId, canEdit, onRemove }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={`Actions for ${itemTitle(item)}`} data-testid={`quotation-item-menu-${item._id}`} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary">
          <MoreVertical className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {projectId && (
          <DropdownMenuItem asChild>
            <Link to={`/windows/${item.windowId}?projectId=${projectId}&roomId=${item.roomId}`}>Open window design</Link>
          </DropdownMenuItem>
        )}
        {canEdit && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-danger focus:text-danger" data-testid={`remove-quotation-item-${item._id}`} onSelect={() => onRemove(item)}>
              Remove from quotation
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotesBox({ quotation, canEdit, onSaved }) {
  const [value, setValue] = useState(quotation.terms || "");
  useEffect(() => setValue(quotation.terms || ""), [quotation.terms]);
  const save = useMutation({
    mutationFn: (terms) => client.patch(`/quotations/${quotation._id}/terms`, { terms }),
    onSuccess: () => {
      toast.success("Note saved");
      onSaved();
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Failed to save note")),
  });
  const dirty = value !== (quotation.terms || "");
  return (
    <div className="rounded-xl bg-secondary/60 p-4 sm:p-5 flex gap-3">
      <FileText className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" strokeWidth={1.6} />
      <div className="flex-1 min-w-0">
        <label htmlFor="quotation-note" className="block text-sm text-foreground mb-2">
          Notes &amp; terms <span className="text-muted-foreground">(printed on the PDF)</span>
        </label>
        {canEdit ? (
          <>
            <textarea
              id="quotation-note"
              data-testid="quotation-note-input"
              rows={4}
              maxLength={2000}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Add any additional notes for this quotation…"
              className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y"
            />
            {dirty && (
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setValue(quotation.terms || "")}>Discard</Button>
                <Button size="sm" data-testid="save-quotation-note" disabled={save.isPending} onClick={() => save.mutate(value)} className="bg-accent hover:bg-accent/90 text-accent-foreground">
                  Save note
                </Button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground whitespace-pre-line">{quotation.terms || "No notes."}</p>
        )}
      </div>
    </div>
  );
}

function Totals({ quotation, editing, setEditing, onSaved }) {
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (editing) {
      setForm({
        discountType: quotation.discountType || "PERCENT",
        discountValue: quotation.discountValue ?? 0,
        gstPercent: quotation.gstPercent ?? 18,
      });
    }
  }, [editing, quotation]);

  const save = useMutation({
    mutationFn: (payload) => client.patch(`/quotations/${quotation._id}/pricing`, payload),
    onSuccess: () => {
      toast.success("Discount and GST updated");
      setEditing(false);
      onSaved();
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Failed to update pricing")),
  });

  // Older quotations have GST inside item amounts; the preview can't separate it, so show stored figures.
  const legacy = quotation.pricingVersion !== 2;
  const active = editing && form;
  const totals = active && !legacy ? previewTotals(quotation.subtotal, form) : quotation;
  const discountLabel = quotation.discountType === "PERCENT" && quotation.discountValue ? `Discount (${quotation.discountValue}%)` : "Discount";
  const gstLabel = quotation.gstPercent != null ? `GST (${quotation.gstPercent}%)` : "GST";
  const numberInput = (key, extra = {}) => (
    <Input
      type="number"
      min={0}
      step="any"
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value === "" ? "" : Number(e.target.value) })}
      className="h-8 w-20 text-right bg-card"
      {...extra}
    />
  );
  const row = "flex items-center justify-between gap-3 text-[15px] text-foreground/80";

  return (
    <div className="space-y-2.5" data-testid="quotation-totals">
      <p className={row}><span>Subtotal</span><span className="tabular-nums">{formatINR(totals.subtotal)}</span></p>
      {active ? (
        <>
          <div className={row}>
            <span>Discount</span>
            <span className="flex items-center gap-1.5">
              {numberInput("discountValue", { "data-testid": "discount-value-input", max: form.discountType === "PERCENT" ? 100 : undefined })}
              <span className="flex rounded-md border border-border overflow-hidden text-xs">
                {["PERCENT", "AMOUNT"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`discount-type-${t}`}
                    onClick={() => setForm({ ...form, discountType: t })}
                    className={cn("px-2 py-1.5", form.discountType === t ? "bg-foreground text-background" : "bg-card text-muted-foreground")}
                  >
                    {t === "PERCENT" ? "%" : "₹"}
                  </button>
                ))}
              </span>
            </span>
          </div>
          {!legacy && <p className="flex justify-end text-xs text-muted-foreground -mt-1.5">-{formatINR(totals.discountAmount)}</p>}
          <div className={row}>
            <span>GST %</span>
            {numberInput("gstPercent", { "data-testid": "gst-percent-input", max: 28 })}
          </div>
          {!legacy && <p className="flex justify-end text-xs text-muted-foreground -mt-1.5">{formatINR(totals.gstAmount)}</p>}
          {legacy && <p className="text-xs text-muted-foreground">Item prices on this quotation include GST. Saving shows them before GST, with GST as its own line.</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
            <Button
              size="sm"
              data-testid="save-pricing-button"
              disabled={save.isPending || form.discountValue === "" || form.gstPercent === ""}
              className="bg-accent hover:bg-accent/90 text-accent-foreground"
              onClick={() => save.mutate(form)}
            >
              Save
            </Button>
          </div>
        </>
      ) : (
        <>
          <p className={row}><span>{discountLabel}</span><span className="tabular-nums">-{formatINR(totals.discountAmount)}</span></p>
          <p className={row}><span>{gstLabel}</span><span className="tabular-nums">{formatINR(totals.gstAmount)}</span></p>
        </>
      )}
      <div className="mt-3 flex items-center justify-between rounded-xl bg-secondary/80 px-5 py-4" data-testid="quotation-grand-total">
        <span className="font-serif text-xl text-foreground">Grand Total</span>
        <span className="font-serif text-2xl text-foreground tabular-nums">{formatINR(totals.grandTotal)}</span>
      </div>
    </div>
  );
}

const QuotationItems = forwardRef(function QuotationItems(
  { quotation, items, projectId, canEditItems, canEditPricing, canEditNotes, pricingEditing, setPricingEditing, onChanged },
  ref,
) {
  const [pendingRemove, setPendingRemove] = useState(null);
  const multiRoom = new Set(items.map((i) => i.roomId)).size > 1;
  const legacy = quotation.pricingVersion !== 2;

  const updateQty = useMutation({
    mutationFn: ({ item, qty }) => client.patch(`/quotations/${quotation._id}/items/${item._id}`, { qty }),
    onSuccess: onChanged,
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't change the quantity")),
  });
  const removeItem = useMutation({
    mutationFn: (item) => client.delete(`/quotations/${quotation._id}/items/${item._id}`),
    onSuccess: () => {
      toast.success("Item removed");
      onChanged();
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Couldn't remove the item")),
  });

  const qtyEditable = canEditItems && !legacy;

  return (
    <section ref={ref} className="bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="quotation-items-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-xl text-foreground">
            Products <span className="font-sans text-base text-muted-foreground">({items.length})</span>
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Selected products for this quotation.</p>
        </div>
        {canEditItems && projectId && (
          <Button variant="outline" asChild data-testid="add-products-button">
            <Link to={`/projects/${projectId}`}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Products
            </Link>
          </Button>
        )}
      </div>

      {canEditItems && legacy && (
        <p className="mb-3 rounded-lg bg-warning/15 text-[hsl(41_60%_32%)] px-3 py-2 text-xs">
          This is an older quotation. Save discount &amp; GST once to enable quantity changes.
        </p>
      )}

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-secondary/60 text-muted-foreground font-mono-spec text-[11px] uppercase tracking-wider">
              <th className="py-3 pl-3 pr-3 text-left font-medium rounded-l-lg">Product</th>
              <th className="py-3 px-3 text-right font-medium">Unit Price</th>
              <th className="py-3 px-3 text-center font-medium">Qty</th>
              <th className="py-3 px-3 text-right font-medium">Amount</th>
              <th className="py-3 pr-3 w-10 rounded-r-lg"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map((it) => (
              <tr key={it._id} data-testid={`quotation-item-row-${it._id}`}>
                <td className="py-3 pl-0 pr-3">
                  <div className="flex items-center gap-4">
                    {it.previewUrl ? (
                      <img src={it.previewUrl} alt="" className="h-[4.5rem] w-[4.5rem] rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="h-[4.5rem] w-[4.5rem] rounded-lg bg-secondary shrink-0" />
                    )}
                    <div className="min-w-0">
                      <p className="text-[15px] text-foreground truncate">{itemTitle(it)}</p>
                      {itemStyle(it) && <p className="text-sm text-muted-foreground truncate">{itemStyle(it)}</p>}
                      {(itemName(it) || multiRoom) && (
                        <p className="text-sm text-muted-foreground truncate">{[itemName(it), multiRoom && it.roomName].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3 px-3 text-right text-[15px] tabular-nums">{formatINR(it.rate)}</td>
                <td className="py-3 px-3 text-center">
                  {qtyEditable ? (
                    <QtyStepper
                      testId={`quotation-item-qty-${it._id}`}
                      value={it.qty}
                      disabled={updateQty.isPending || removeItem.isPending}
                      onChange={(qty) => updateQty.mutate({ item: it, qty })}
                    />
                  ) : (
                    <span className="tabular-nums">{it.qty}</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right text-[15px] font-medium tabular-nums">{formatINR(it.amount)}</td>
                <td className="py-3 pr-0 text-right">
                  <ItemMenu item={it} projectId={projectId} canEdit={canEditItems && items.length > 1} onRemove={setPendingRemove} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile list */}
      <ul className="md:hidden divide-y divide-border">
        {items.map((it) => (
          <li key={it._id} className="py-3 flex gap-3">
            {it.previewUrl ? <img src={it.previewUrl} alt="" className="h-16 w-16 rounded-lg object-cover" /> : <div className="h-16 w-16 rounded-lg bg-secondary" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-foreground truncate">{itemTitle(it)}</p>
                <ItemMenu item={it} projectId={projectId} canEdit={canEditItems && items.length > 1} onRemove={setPendingRemove} />
              </div>
              <p className="text-xs text-muted-foreground truncate">{[itemStyle(it), itemName(it)].filter(Boolean).join(" · ")}</p>
              <div className="mt-2 flex items-center justify-between">
                {qtyEditable ? (
                  <QtyStepper value={it.qty} disabled={updateQty.isPending} onChange={(qty) => updateQty.mutate({ item: it, qty })} />
                ) : (
                  <span className="text-sm text-muted-foreground">Qty {it.qty}</span>
                )}
                <span className="font-medium tabular-nums">{formatINR(it.amount)}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-5 pt-5 border-t border-border grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-6">
        <NotesBox quotation={quotation} canEdit={canEditNotes} onSaved={onChanged} />
        <div className="lg:border-l lg:border-border lg:pl-6">
          <Totals quotation={quotation} editing={pricingEditing} setEditing={setPricingEditing} onSaved={onChanged} />
          {canEditPricing && !pricingEditing && (
            <button type="button" onClick={() => setPricingEditing(true)} data-testid="edit-pricing-button" className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline">
              <Pencil className="h-3 w-3" /> Edit discount &amp; GST
            </button>
          )}
        </div>
      </div>

      <AlertDialog open={!!pendingRemove} onOpenChange={(o) => !o && setPendingRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this product from the quotation?</AlertDialogTitle>
            <AlertDialogDescription>The totals are recalculated. The window's design and the project cart are not changed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-danger hover:bg-danger/90" data-testid="confirm-remove-quotation-item" onClick={() => removeItem.mutate(pendingRemove)}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
});

export default QuotationItems;
